/**
 * Pure helpers for resolving inline `[[src:…]]` citations to verifiable spans.
 *
 * Two concerns, both pure (no DOM, no React) so they unit-test cleanly:
 *
 * 1. {@link buildCitationLookup} indexes a card's structured citation
 *    provenance (`frontmatter.citations`) by span ref, so the inline citation
 *    renderer can look up the explicit cited lines + verbatim snippet behind a
 *    `[[src:<ref>]]` token.
 * 2. {@link resolveCitationSpan} maps a citation onto the CURRENT source body —
 *    the heart of **stale-range handling**. Sources are versioned and editable,
 *    so a stored `#L<a>-<b>` can drift after the citation was made. Rather than
 *    silently highlight whatever now sits at those numbers (which could be the
 *    wrong text), it verifies the cited snippet still matches, re-anchors by
 *    content search when it moved, and refuses to highlight when it cannot be
 *    located — failing loud (a "source changed" banner), never silently wrong.
 *
 * Nothing is logged (HIPAA); source text is PHI and stays in memory only.
 */
import type { SourceCitation } from "../../types";

/** The `[[src:derivative:<id>]]` citation namespace for synced-back facts. */
const DERIVATIVE_PREFIX = "derivative:";

/** A reveal target: which source + the cited span to scroll to and highlight. */
export interface SourceRevealTarget {
  /** The bare source/document id to open (any `derivative:` prefix stripped). */
  sourceId: string;
  /** The explicit cited physical line numbers (the highlight envelope). */
  lines: number[];
  /** The verbatim cited text (the content-anchor for stale detection). */
  snippet: string;
  /**
   * When true the citation points at a generated **derivative document** (a
   * synced-back fact, ADR-011), not a raw `type=source` — so "Go to source"
   * opens it in the Documents editor rather than the Sources viewer.
   */
  openInEditor?: boolean;
}

/**
 * A pending source-reveal carried through the Sources workspace tree: the
 * target span plus a monotonic `key` so a repeat reveal of the same span still
 * re-triggers the scroll + flash.
 */
export interface SourceReveal {
  /** The cited span to scroll to + highlight. */
  target: SourceRevealTarget;
  /** Monotonic key — incremented on every reveal so repeats re-flash. */
  key: number;
}

/** The outcome of resolving a citation span against the current source body. */
export interface ResolvedSpan {
  /**
   * The 1-based physical line numbers to highlight in the current source, or
   * empty when the cited text can no longer be located (caller shows a banner +
   * the original excerpt instead of highlighting the wrong lines).
   */
  lines: number[];
  /** True when the source no longer matches the cited snippet at those lines. */
  stale: boolean;
  /** True when the cited text could not be located in the current source. */
  unlocatable: boolean;
}

/**
 * Build a `ref → SourceCitation` lookup from a card's structured citations.
 *
 * Populates map with full ref (`c.ref`), stripped base ref (without `#L...`),
 * and `c.source_id`. Also ensures lookup resolves keys whether the query ref or
 * citation ref has `#L` envelope or not.
 *
 * @param citations - The card frontmatter's `citations` list (may be absent).
 * @returns A map keyed by span ref (first occurrence wins; blanks skipped).
 */
export function buildCitationLookup(
  citations?: SourceCitation[] | null,
): Map<string, SourceCitation> {
  const map = new Map<string, SourceCitation>();
  for (const c of citations ?? []) {
    const ref = typeof c.ref === "string" ? c.ref.trim() : "";
    if (ref !== "") {
      if (!map.has(ref)) {
        map.set(ref, c);
      }
      // Ref without quote if it has colon-quote syntax (e.g. `doc-1#S2: "quote"` -> `doc-1#S2`)
      const quoteColonAt = ref.indexOf(': "');
      if (quoteColonAt !== -1) {
        const withoutQuote = ref.slice(0, quoteColonAt).trim();
        if (withoutQuote !== "" && !map.has(withoutQuote)) {
          map.set(withoutQuote, c);
        }
      }
      const hashAt = ref.indexOf("#");
      const baseRef = (hashAt === -1 ? ref : ref.slice(0, hashAt)).trim();
      if (baseRef !== "" && !map.has(baseRef)) {
        map.set(baseRef, c);
      }
    }
    const sourceId = typeof c.source_id === "string" ? c.source_id.trim() : "";
    if (sourceId !== "" && !map.has(sourceId)) {
      map.set(sourceId, c);
    }
  }

  // Wrap get to handle any query ref with or without quote or #L/#S envelope
  const originalGet = map.get.bind(map);
  map.get = (key: string): SourceCitation | undefined => {
    const direct = originalGet(key);
    if (direct !== undefined) return direct;
    const quoteColonAt = key.indexOf(': "');
    if (quoteColonAt !== -1) {
      const withoutQuote = key.slice(0, quoteColonAt).trim();
      if (withoutQuote !== "") {
        const fromWithoutQuote = originalGet(withoutQuote);
        if (fromWithoutQuote !== undefined) return fromWithoutQuote;
      }
    }
    const hashAt = key.indexOf("#");
    if (hashAt !== -1) {
      const base = key.slice(0, hashAt).trim();
      if (base !== "") {
        const fromBase = originalGet(base);
        if (fromBase !== undefined) return fromBase;
      }
    }
    return undefined;
  };

  return map;
}

/**
 * Build a reveal target for a citation ref, preferring structured provenance.
 *
 * With a resolved {@link SourceCitation} the target carries the explicit cited
 * lines + verbatim snippet. Without one (older pages / non-ingestion edits / inline
 * tokens) it parses embedded snippets from `: "..."` and falls back to `#L<a>-<b>`
 * line ranges or empty lines for `#S<n>` sentence coordinates so "Go to source"
 * still works.
 *
 * @param ref - The citation span ref (`<source_id>#L<a>-<b>` or `<source_id>#S<n>: "<quote>"`).
 * @param citation - The resolved structured citation for this ref, if any.
 * @returns The reveal target to hand {@link RevealSourceContext}.
 */
export function revealTargetFromRef(
  ref: string,
  citation?: SourceCitation,
): SourceRevealTarget {
  if (citation) {
    return withDerivativeRouting({
      sourceId: citation.source_id,
      lines: citation.lines ?? [],
      snippet: citation.snippet ?? "",
    });
  }
  const quoteMatch = /:\s*"([^"]+)"/.exec(ref);
  const snippet = quoteMatch ? quoteMatch[1].trim() : "";
  const colonQuoteAt = ref.indexOf(': "');
  const refWithoutQuote =
    colonQuoteAt !== -1 ? ref.slice(0, colonQuoteAt).trim() : ref;
  const hashAt = refWithoutQuote.indexOf("#");
  const sourceId = (
    hashAt === -1 ? refWithoutQuote : refWithoutQuote.slice(0, hashAt)
  ).trim();
  const range = /#L(\d+)(?:-L?(\d+))?/.exec(refWithoutQuote);
  const lines: number[] = [];
  if (range) {
    const a = Number.parseInt(range[1], 10);
    const b = range[2] ? Number.parseInt(range[2], 10) : a;
    for (let n = Math.min(a, b); n <= Math.max(a, b); n += 1) lines.push(n);
  }
  return withDerivativeRouting({ sourceId, lines, snippet });
}

/**
 * Extract structured citations from text containing `[[src:...]]` markers.
 *
 * Scans text for `[[src:...]]` tokens and maps each to a structured
 * {@link SourceCitation}, extracting `source_id`, `snippet` from `: "..."`,
 * `sentence_number` from `#S<n>`, and physical lines from `#L<min>-<max>`.
 *
 * @param text - The text containing `[[src:...]]` markers.
 * @returns Array of structured citations in order of first appearance.
 */
export function extractCitationsFromText(text: string): SourceCitation[] {
  if (!text) return [];
  const citations: SourceCitation[] = [];
  const seenRefs = new Set<string>();
  const tokenRegex = /\[\[src:([^\]]+)\]\]/g;
  let match: RegExpExecArray | null;

  while ((match = tokenRegex.exec(text)) !== null) {
    const ref = match[1].trim();
    if (!ref || seenRefs.has(ref)) continue;
    seenRefs.add(ref);

    const quoteMatch = /:\s*"([^"]+)"/.exec(ref);
    const snippet = quoteMatch ? quoteMatch[1].trim() : "";

    const colonQuoteAt = ref.indexOf(': "');
    const refWithoutQuote =
      colonQuoteAt !== -1 ? ref.slice(0, colonQuoteAt).trim() : ref;

    const hashAt = refWithoutQuote.indexOf("#");
    const sourceId = (
      hashAt === -1 ? refWithoutQuote : refWithoutQuote.slice(0, hashAt)
    ).trim();

    const sentenceMatch = /#S(\d+)/.exec(refWithoutQuote);
    const sentenceNumber = sentenceMatch
      ? Number.parseInt(sentenceMatch[1], 10)
      : undefined;

    const lineRangeMatch = /#L(\d+)(?:-L?(\d+))?/.exec(refWithoutQuote);
    const lines: number[] = [];
    if (lineRangeMatch) {
      const a = Number.parseInt(lineRangeMatch[1], 10);
      const b = lineRangeMatch[2] ? Number.parseInt(lineRangeMatch[2], 10) : a;
      for (let n = Math.min(a, b); n <= Math.max(a, b); n += 1) {
        lines.push(n);
      }
    }

    const citation: SourceCitation = {
      ref,
      source_id: sourceId,
      lines,
      snippet,
      ...(sentenceNumber !== undefined
        ? { sentence_number: sentenceNumber }
        : {}),
    };

    citations.push(citation);
  }

  return citations;
}

/**
 * Rewrite a reveal target for the `derivative:` citation namespace (ADR-011).
 *
 * A synced-back fact cites `[[src:derivative:<doc_id>]]`, so the raw source id
 * is `derivative:<doc_id>` — which is NOT a document id and 404s if opened
 * verbatim. This strips the prefix to the bare `<doc_id>` and flags the target
 * for the Documents editor (the derivative is a `type=markdown` doc, not a
 * `type=source`). Non-derivative targets pass through unchanged.
 *
 * @param target - The reveal target built from a citation/ref.
 * @returns The target, with any `derivative:` prefix stripped + editor-routed.
 */
function withDerivativeRouting(target: SourceRevealTarget): SourceRevealTarget {
  if (!target.sourceId.startsWith(DERIVATIVE_PREFIX)) return target;
  return {
    ...target,
    sourceId: target.sourceId.slice(DERIVATIVE_PREFIX.length),
    openInEditor: true,
  };
}

/** Trim a line for whitespace-insensitive comparison against the snippet. */
function norm(line: string): string {
  return line.trim();
}

/** The cited lines' current text, normalized; null marks an out-of-range line. */
function currentAt(bodyLines: string[], lineNo: number): string | null {
  return lineNo >= 1 && lineNo <= bodyLines.length
    ? norm(bodyLines[lineNo - 1])
    : null;
}

/** Do the cited line numbers still hold the cited snippet text? */
function matchesAtCitedLines(
  citedLines: number[],
  snippetLines: string[],
  bodyLines: string[],
  snippet: string,
): boolean {
  if (citedLines.length === 0) return false;
  if (
    citedLines.length === snippetLines.length &&
    citedLines.every((n, i) => currentAt(bodyLines, n) === snippetLines[i])
  ) {
    return true;
  }
  const cStart = Math.min(...citedLines);
  const cEnd = Math.max(...citedLines);
  if (cStart >= 1 && cEnd <= bodyLines.length) {
    const citedText = bodyLines.slice(cStart - 1, cEnd).join("\n");
    const trimmedSnippet = snippet.trim();
    if (
      citedText.includes(snippet) ||
      (trimmedSnippet !== "" && citedText.includes(trimmedSnippet))
    ) {
      return true;
    }
  }
  return false;
}

/** Find the snippet's lines as a contiguous run; returns its 1-based start or -1. */
function findContiguousRun(
  snippetLines: string[],
  bodyLines: string[],
): number {
  const normalized = bodyLines.map(norm);
  const last = normalized.length - snippetLines.length;
  for (let start = 0; start <= last; start += 1) {
    if (snippetLines.every((s, i) => normalized[start + i] === s))
      return start + 1;
  }
  return -1;
}

/** Find lines spanned by snippet as a substring in body, picking closest to hint. */
function findSubstringRun(
  snippet: string,
  body: string,
  hintLine?: number,
): number[] | null {
  const needle = snippet.trim();
  const searchStr = body.includes(snippet)
    ? snippet
    : body.includes(needle)
      ? needle
      : null;
  if (!searchStr) return null;

  let bestStart = 0;
  let bestDist = Number.POSITIVE_INFINITY;
  let pos = body.indexOf(searchStr);
  const hint = hintLine ?? 1;

  while (pos !== -1) {
    const startLine = body.slice(0, pos).split("\n").length;
    const dist = Math.abs(startLine - hint);
    if (dist < bestDist) {
      bestDist = dist;
      bestStart = pos;
    }
    pos = body.indexOf(searchStr, pos + 1);
  }

  const startLine = body.slice(0, bestStart).split("\n").length;
  const lastCharOffset = bestStart + Math.max(0, searchStr.length - 1);
  const endLine = body.slice(0, lastCharOffset).split("\n").length;
  const lines: number[] = [];
  for (let n = startLine; n <= endLine; n += 1) {
    lines.push(n);
  }
  return lines;
}

/** Clamp cited line numbers to those that exist in the current body. */
function inRange(citedLines: number[], bodyLineCount: number): number[] {
  return citedLines.filter((n) => n >= 1 && n <= bodyLineCount);
}

/**
 * Resolve a citation's highlight span against the current source body.
 *
 * Stale-range handling, in order: (1) no snippet to verify against → trust the
 * cited line numbers as-is (best-effort, not flagged stale); (2) the cited lines
 * still hold the cited snippet → fresh, highlight them; (3) the snippet moved →
 * re-anchor to the contiguous run that now holds it, flagged stale; (4) the
 * snippet is gone → unlocatable, highlight nothing (the caller surfaces the
 * original excerpt + a "source changed" banner rather than mislead).
 *
 * @param citedLines - The explicit cited physical line numbers.
 * @param snippet - The verbatim cited text (newline-joined), or "" when none.
 * @param body - The current full source body text.
 * @returns The resolved highlight span with stale/unlocatable flags.
 */
export function resolveCitationSpan(
  citedLines: number[],
  snippet: string,
  body: string,
): ResolvedSpan {
  const bodyLines = body.split("\n");
  const snippetLines = snippet.split("\n").map(norm);
  const hasSnippet = snippet.trim() !== "";
  if (!hasSnippet) {
    return {
      lines: inRange(citedLines, bodyLines.length),
      stale: false,
      unlocatable: false,
    };
  }
  if (matchesAtCitedLines(citedLines, snippetLines, bodyLines, snippet)) {
    return { lines: citedLines, stale: false, unlocatable: false };
  }
  const start = findContiguousRun(snippetLines, bodyLines);
  if (start !== -1) {
    const lines = snippetLines.map((_, i) => start + i);
    return { lines, stale: true, unlocatable: false };
  }
  const subLines = findSubstringRun(snippet, body, citedLines[0]);
  if (subLines !== null) {
    return { lines: subLines, stale: true, unlocatable: false };
  }
  return { lines: [], stale: true, unlocatable: true };
}
